/**
 * TanStack Query hooks for the Allergens admin feature.
 *
 * The whole allergen surface (list, create, update, delete, deactivate,
 * reorder) is missing from the generated `paths` map — every call uses
 * the `as never` cast against the typed client. The runtime is the same
 * `api` instance, so auth, retries, and ApiError parsing apply.
 *
 * TODO: drop the casts when the backend OpenAPI exposes:
 *   - `GET /api/v1/admin/allergens?onlyActive=…`
 *   - `POST /api/v1/admin/allergens`
 *   - `PUT /api/v1/admin/allergens/{id}`
 *   - `DELETE /api/v1/admin/allergens/{id}`
 *   - `PATCH /api/v1/admin/allergens/{id}/deactivate`
 *   - `POST /api/v1/admin/allergens/reorder`
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@favelafood/shared/api/client';

export interface AllergenResponse {
  id: string;
  code: string;
  label: string;
  displayOrder: number;
  isActive: boolean;
  referencingProductCount?: number | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateAllergenRequest {
  code: string;
  label: string;
  displayOrder?: number;
  isActive?: boolean;
}

export interface UpdateAllergenRequest {
  code?: string;
  label?: string;
  displayOrder?: number;
  isActive?: boolean;
}

export interface AllergenListOpts {
  onlyActive: boolean;
}

export const allergensKeys = {
  all: ['allergens'] as const,
  list: (opts: AllergenListOpts) => [...allergensKeys.all, 'list', opts] as const,
};

export function useAllergensList(
  opts: AllergenListOpts,
): UseQueryResult<AllergenResponse[]> {
  return useQuery({
    queryKey: allergensKeys.list(opts),
    queryFn: async () => {
      const { data, error } = await api.GET(
        ('/api/v1/admin/allergens' + `?onlyActive=${opts.onlyActive}`) as never,
      );
      if (error) throw error as Error;
      return (data as AllergenResponse[] | undefined) ?? [];
    },
  });
}

export function useCreateAllergen(): UseMutationResult<
  AllergenResponse,
  Error,
  CreateAllergenRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body) => {
      const { data, error } = await api.POST(
        '/api/v1/admin/allergens' as never,
        { body } as never,
      );
      if (error) throw error as Error;
      return data as AllergenResponse;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: allergensKeys.all });
    },
  });
}

export function useUpdateAllergen(): UseMutationResult<
  AllergenResponse,
  Error,
  { id: string; body: UpdateAllergenRequest }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }) => {
      const { data, error } = await api.PUT(
        `/api/v1/admin/allergens/${id}` as never,
        { body } as never,
      );
      if (error) throw error as Error;
      return data as AllergenResponse;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: allergensKeys.all });
    },
  });
}

export function useDeactivateAllergen(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const { error } = await api.PATCH(
        `/api/v1/admin/allergens/${id}/deactivate` as never,
      );
      if (error) throw error as Error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: allergensKeys.all });
    },
  });
}

export function useDeleteAllergen(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const { error } = await api.DELETE(`/api/v1/admin/allergens/${id}` as never);
      if (error) throw error as Error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: allergensKeys.all });
    },
  });
}

export function useReorderAllergens(): UseMutationResult<void, Error, { ids: string[] }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ids }) => {
      const { error } = await api.POST(
        '/api/v1/admin/allergens/reorder' as never,
        { body: { ids } } as never,
      );
      if (error) throw error as Error;
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: allergensKeys.all });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: allergensKeys.all });
    },
  });
}
